import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Title1,
  Subtitle2,
  TabList,
  Tab,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  MessageBar,
  Input,
  Button,
  Divider,
  tokens,
} from '@fluentui/react-components'
import { Dismiss12Regular } from '@fluentui/react-icons'
import { apiFetchJson } from '../lib/apiFetch'
import { canManageService, useMe } from '../lib/me'
import { formatSyncAge, syncFreshnessColor } from '../lib/syncFreshness'
import Lozenge from '../components/Lozenge'
import ServiceIconPicker from '../components/ServiceIconPicker'
import type { PeopleForceResponse, ServiceConfigResponse } from '../types'

// Matches the partition key /api/sync-runs?serviceId=peopleforce already uses for this — not a
// real MODULES ServiceModule, but /api/service-config keys by plain string, so any id works as
// long as it's used consistently (Dashboard.tsx's PeopleForce card reads the same key).
const SERVICE_ID = 'peopleforce'

// Three tabs mirror the same separation-of-concerns principle as ServiceConfig.tsx's per-service
// tabs (Licenses / License types / User links / Settings), even though the concept names differ —
// PeopleForce has no license/type data, but the same idea of "problems", "raw data", and "config"
// each getting their own tab still applies: mismatches used to sit stacked above the full synced
// list on one tab, making the actual problem list harder to scan.
type TabValue = 'mismatches' | 'all' | 'settings'

const VALID_TABS: TabValue[] = ['mismatches', 'all', 'settings']

// Fixed per-column pixel widths + whiteSpace: nowrap, same fix as pages/Users.tsx — otherwise
// long emails run past their column and visually overlap the next one instead of the table
// scrolling horizontally.
const MISMATCH_COLUMN_WIDTHS = {
  email: 260,
  displayName: 180,
  entraCountry: 130,
  entraCity: 130,
  peopleForceLocation: 160,
} as const

const SYNCED_COLUMN_WIDTHS = {
  email: 260,
  displayName: 180,
  location: 160,
  lastSynced: 170,
} as const

function cellStyle(width: number): React.CSSProperties {
  return { width, minWidth: width, maxWidth: width, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
}

interface SyncRun {
  startedAt: string
  finishedAt?: string
  recordsProcessed: number
  errorsJson: string
}

function usePeopleForceData() {
  const [data, setData] = useState<PeopleForceResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    apiFetchJson<PeopleForceResponse>('/api/people-force')
      .then(setData)
      .catch(e => setError(String(e)))
  }, [])

  return { data, error }
}

function MismatchesTab() {
  const { data, error } = usePeopleForceData()

  if (error) return <p>Error: {error}</p>
  if (!data) return <Spinner label="Loading PeopleForce data..." style={{ marginTop: 24 }} />
  if (data.mismatches.length === 0) {
    return <p>No location mismatches — every matched person's PeopleForce location agrees with Entra.</p>
  }

  return (
    <div>
      <Subtitle2 style={{ display: 'block', marginBottom: 8 }}>
        Location mismatches ({data.mismatches.length})
      </Subtitle2>
      <div style={{ overflowX: 'auto' }}>
        <Table style={{ tableLayout: 'fixed' }}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={cellStyle(MISMATCH_COLUMN_WIDTHS.email)}>Email</TableHeaderCell>
              <TableHeaderCell style={cellStyle(MISMATCH_COLUMN_WIDTHS.displayName)}>Display name</TableHeaderCell>
              <TableHeaderCell style={cellStyle(MISMATCH_COLUMN_WIDTHS.entraCountry)}>Entra country</TableHeaderCell>
              <TableHeaderCell style={cellStyle(MISMATCH_COLUMN_WIDTHS.entraCity)}>Entra city</TableHeaderCell>
              <TableHeaderCell style={cellStyle(MISMATCH_COLUMN_WIDTHS.peopleForceLocation)}>PeopleForce location</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.mismatches.map(m => (
              <TableRow key={m.personEmail}>
                <TableCell style={cellStyle(MISMATCH_COLUMN_WIDTHS.email)}>{m.personEmail}</TableCell>
                <TableCell style={cellStyle(MISMATCH_COLUMN_WIDTHS.displayName)}>{m.personDisplayName}</TableCell>
                <TableCell style={cellStyle(MISMATCH_COLUMN_WIDTHS.entraCountry)}>{m.entraCountry || '—'}</TableCell>
                <TableCell style={cellStyle(MISMATCH_COLUMN_WIDTHS.entraCity)}>{m.entraCity || '—'}</TableCell>
                <TableCell style={cellStyle(MISMATCH_COLUMN_WIDTHS.peopleForceLocation)}>{m.peopleForceLocation}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function AllSyncedTab() {
  const { data, error } = usePeopleForceData()

  if (error) return <p>Error: {error}</p>
  if (!data) return <Spinner label="Loading PeopleForce data..." style={{ marginTop: 24 }} />

  return (
    <div>
      <Subtitle2 style={{ display: 'block', marginBottom: 8 }}>
        Everyone synced from PeopleForce ({data.persons.length} total)
      </Subtitle2>
      <div style={{ overflowX: 'auto' }}>
        <Table style={{ tableLayout: 'fixed' }}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={cellStyle(SYNCED_COLUMN_WIDTHS.email)}>Email</TableHeaderCell>
              <TableHeaderCell style={cellStyle(SYNCED_COLUMN_WIDTHS.displayName)}>Display name</TableHeaderCell>
              <TableHeaderCell style={cellStyle(SYNCED_COLUMN_WIDTHS.location)}>Location</TableHeaderCell>
              <TableHeaderCell style={cellStyle(SYNCED_COLUMN_WIDTHS.lastSynced)}>Last synced</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.persons.map(p => (
              <TableRow key={p.email}>
                <TableCell style={cellStyle(SYNCED_COLUMN_WIDTHS.email)}>{p.email}</TableCell>
                <TableCell style={cellStyle(SYNCED_COLUMN_WIDTHS.displayName)}>{p.displayName ?? '—'}</TableCell>
                <TableCell style={cellStyle(SYNCED_COLUMN_WIDTHS.location)}>{p.locationName ?? '—'}</TableCell>
                <TableCell style={cellStyle(SYNCED_COLUMN_WIDTHS.lastSynced)}>{p.lastSyncedAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

/** Location words to exclude from "Location mismatches" entirely — e.g. "Army"/"Outsource" are
 * real values HR uses in PeopleForce's location field, but they're not geographic locations at
 * all and will never agree with Entra's country/city, so flagging them was pure noise rather than
 * a real HR/Entra disagreement worth an admin's attention. See lib/serviceConfig.ts's
 * `ServiceConfig.ignoredLocations` and lib/peopleForceMatching.ts. */
function IgnoredLocationsField() {
  const [words, setWords] = useState<string[]>([])
  const [newWord, setNewWord] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function load() {
    apiFetchJson<ServiceConfigResponse>('/api/service-config')
      .then(res => setWords(res.config[SERVICE_ID]?.ignoredLocations ?? []))
      .catch(e => setError(String(e)))
      .finally(() => setLoaded(true))
  }

  useEffect(load, [])

  async function save(next: string[]) {
    setWords(next)
    setError(null)
    try {
      await apiFetchJson('/api/service-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serviceId: SERVICE_ID, ignoredLocations: next }),
      })
    } catch (e) {
      // Revert the optimistic update to whatever the server actually has, so a failed
      // add/remove doesn't look like it silently succeeded.
      setError(String(e))
      load()
    }
  }

  function addWord() {
    const trimmed = newWord.trim()
    if (!trimmed || words.some(w => w.toLowerCase() === trimmed.toLowerCase())) {
      setNewWord('')
      return
    }
    save([...words, trimmed])
    setNewWord('')
  }

  function removeWord(word: string) {
    save(words.filter(w => w !== word))
  }

  return (
    <div>
      <div style={{ marginBottom: 4, fontWeight: 600 }}>Ignored locations</div>
      <p style={{ margin: '0 0 8px', fontSize: 13, color: tokens.colorNeutralForeground3, maxWidth: 480 }}>
        A PeopleForce location value listed here never counts as a mismatch, even if it disagrees
        with Entra — for values like "Army" or "Outsource" that were never meant to be a real
        country/city.
      </p>
      {words.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {words.map(w => (
            <Lozenge key={w} color="subtle">
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {w}
                <Button
                  appearance="transparent"
                  size="small"
                  icon={<Dismiss12Regular />}
                  aria-label={`Remove "${w}"`}
                  onClick={() => removeWord(w)}
                />
              </span>
            </Lozenge>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          disabled={!loaded}
          placeholder="e.g. Army"
          value={newWord}
          onChange={(_e, d) => setNewWord(d.value)}
          onKeyDown={e => e.key === 'Enter' && addWord()}
          style={{ minWidth: 180 }}
        />
        <Button onClick={addWord} disabled={!loaded || !newWord.trim()}>
          Add
        </Button>
      </div>
      {error && (
        <MessageBar intent="error" style={{ marginTop: 8 }}>
          {error}
        </MessageBar>
      )}
    </div>
  )
}

/** Alias -> canonical country-name pairs for "Location mismatch" comparison — the *only* source
 * of these (no built-in defaults in code, deliberately: a hardcoded table is exactly what broke
 * silently when Entra/Microsoft renamed Turkey's display name to "Türkiye" and nobody could fix
 * it without a code change + deploy). Add every pair that matters to your org here. See
 * lib/serviceConfig.ts's `ServiceConfig.countryAliases` and lib/peopleForceMatching.ts. */
function CountryAliasesField() {
  const [aliases, setAliases] = useState<Record<string, string>>({})
  const [newAlias, setNewAlias] = useState('')
  const [newCanonical, setNewCanonical] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function load() {
    apiFetchJson<ServiceConfigResponse>('/api/service-config')
      .then(res => setAliases(res.config[SERVICE_ID]?.countryAliases ?? {}))
      .catch(e => setError(String(e)))
      .finally(() => setLoaded(true))
  }

  useEffect(load, [])

  async function save(next: Record<string, string>) {
    setAliases(next)
    setError(null)
    try {
      await apiFetchJson('/api/service-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serviceId: SERVICE_ID, countryAliases: next }),
      })
    } catch (e) {
      // Revert the optimistic update to whatever the server actually has, so a failed
      // add/remove doesn't look like it silently succeeded.
      setError(String(e))
      load()
    }
  }

  function addAlias() {
    const alias = newAlias.trim().toLowerCase()
    const canonical = newCanonical.trim()
    if (!alias || !canonical) return
    save({ ...aliases, [alias]: canonical })
    setNewAlias('')
    setNewCanonical('')
  }

  function removeAlias(alias: string) {
    const rest = { ...aliases }
    delete rest[alias]
    save(rest)
  }

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ marginBottom: 4, fontWeight: 600 }}>Country name aliases</div>
      <p style={{ margin: '0 0 8px', fontSize: 13, color: tokens.colorNeutralForeground3, maxWidth: 480 }}>
        No built-in defaults — add a pair here whenever a disagreement turns out to be just two
        names for the same place (e.g. "usa" → "United States", "türkiye" → "Turkey"). The right
        side is the canonical name; point every variant of the same place at the same one. Matching
        trims and lowercases, and follows alias chains in both directions, so "united states" →
        "USA" also resolves — but a single shared canonical is easiest to reason about.
      </p>
      {Object.keys(aliases).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {Object.entries(aliases).map(([alias, canonical]) => (
            <Lozenge key={alias} color="subtle">
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {alias} → {canonical}
                <Button
                  appearance="transparent"
                  size="small"
                  icon={<Dismiss12Regular />}
                  aria-label={`Remove "${alias}" → "${canonical}"`}
                  onClick={() => removeAlias(alias)}
                />
              </span>
            </Lozenge>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Input
          disabled={!loaded}
          placeholder="Alias (e.g. Holland)"
          value={newAlias}
          onChange={(_e, d) => setNewAlias(d.value)}
          onKeyDown={e => e.key === 'Enter' && addAlias()}
          style={{ minWidth: 160 }}
        />
        <span>→</span>
        <Input
          disabled={!loaded}
          placeholder="Canonical (e.g. Netherlands)"
          value={newCanonical}
          onChange={(_e, d) => setNewCanonical(d.value)}
          onKeyDown={e => e.key === 'Enter' && addAlias()}
          style={{ minWidth: 180 }}
        />
        <Button onClick={addAlias} disabled={!loaded || !newAlias.trim() || !newCanonical.trim()}>
          Add
        </Button>
      </div>
      {error && (
        <MessageBar intent="error" style={{ marginTop: 8 }}>
          {error}
        </MessageBar>
      )}
    </div>
  )
}

function SettingsTab() {
  const [lastRun, setLastRun] = useState<SyncRun | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    apiFetchJson<{ runs: SyncRun[] }>(`/api/sync-runs?serviceId=${SERVICE_ID}`)
      .then(res => setLastRun(res.runs[0] ?? null))
      .catch(e => setError(String(e)))
  }, [])

  const lastSyncedAt = lastRun ? (lastRun.finishedAt ?? lastRun.startedAt) : null
  const errors = lastRun ? (JSON.parse(lastRun.errorsJson) as string[]) : []

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <ServiceIconPicker serviceId={SERVICE_ID} />
      </div>

      <MessageBar intent="info">
        PeopleForce syncs automatically on a schedule (see `timer/src/functions/peopleForceSyncTimer.ts`
        for the current interval). Manual sync isn't available here because its API key lives in
        Key Vault, which the API (Static Web App Free tier) has no access to — only the scheduled
        timer function can reach it. See docs/ARCHITECTURE.md.
      </MessageBar>

      <div style={{ marginTop: 16, display: 'flex', gap: 24, alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>Last sync</div>
          <Lozenge color={syncFreshnessColor(lastSyncedAt)}>{formatSyncAge(lastSyncedAt)}</Lozenge>
        </div>
        {errors.length > 0 && (
          <div>
            <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>Last sync errors</div>
            <Lozenge color="danger">{errors.length} errors</Lozenge>
          </div>
        )}
      </div>
      {errors.length > 0 && (
        <MessageBar intent="error" style={{ marginTop: 16 }}>
          {errors.join(' ')}
        </MessageBar>
      )}
      {error && <p>Error: {error}</p>}

      <Divider style={{ marginTop: 24, marginBottom: 16 }} />
      <IgnoredLocationsField />
      <CountryAliasesField />
    </div>
  )
}

export default function PeopleForce() {
  const [searchParams, setSearchParams] = useSearchParams()
  const me = useMe()
  const canManage = canManageService(me, 'peopleforce')
  const requestedTab = searchParams.get('tab')
  let activeTab: TabValue = VALID_TABS.includes(requestedTab as TabValue) ? (requestedTab as TabValue) : 'mismatches'
  if (activeTab === 'settings' && !canManage) activeTab = 'mismatches'

  function handleTabSelect(value: TabValue) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', value)
      return next
    })
  }

  return (
    <div>
      <Title1>PeopleForce</Title1>
      <Subtitle2 style={{ display: 'block', marginTop: 4, marginBottom: 16 }}>
        HR location cross-check against Entra ID
      </Subtitle2>

      <TabList selectedValue={activeTab} onTabSelect={(_e, data) => handleTabSelect(data.value as TabValue)} style={{ marginBottom: 16 }}>
        <Tab value="mismatches">Mismatches</Tab>
        <Tab value="all">All synced</Tab>
        {canManage && <Tab value="settings">Settings</Tab>}
      </TabList>

      {activeTab === 'mismatches' && <MismatchesTab />}
      {activeTab === 'all' && <AllSyncedTab />}
      {activeTab === 'settings' && canManage && <SettingsTab />}
    </div>
  )
}
